import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FiArrowLeft } from "react-icons/fi";
import Dialog from "../../../components/UI/Dialog";
import { Action, request } from "../shared";
import { useLanguage } from "../../../contexts/LanguageContext";
import CatalogPanel, { CatalogFilters, EMPTY_FILTERS } from "./CatalogPanel";
import { DecorationSlots, SlotPick, SkinPreview, SkinSettings } from "./SkinEditor";
import { Item, Saved, TEAM_T, availableWears, slotOf, splitName } from "./data";

const KINDS = ["weapon", "knife", "glove"] as const;

const Shell = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  min-height: 0;
  min-width: 0;
`;
const Head = styled.header`
  display: grid;
  gap: 12px;
  padding: 22px 64px 14px 28px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  .eyebrow {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.primary};
  }
  h2 {
    padding-right: 0;
    font-size: 22px;
    line-height: 28px;
    overflow-wrap: anywhere;
  }
  @media (min-width: 861px) and (max-height: 820px) {
    gap: 4px;
    padding: 14px 64px 10px 28px;
  }
  @media (max-width: 600px) {
    padding: 14px 56px 12px 16px;
    .eyebrow {
      display: none;
    }
    h2 {
      font-size: 18px;
      line-height: 24px;
    }
  }
`;
const Tabs = styled.div`
  display: flex;
  justify-self: start;
  max-width: 100%;
  gap: 4px;
  padding: 4px;
  overflow-x: auto;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.input};
  border: 1px solid ${(p) => p.theme.colors.border};
  button {
    flex: none;
    min-height: 38px;
    padding: 6px 16px;
    border-radius: 9px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
    &[aria-selected="true"] {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
      box-shadow: inset 0 -2px 0 ${(p) => p.theme.colors.primary};
    }
  }
`;
const CatalogArea = styled.div`
  min-height: 0;
  padding: 16px 24px 16px 28px;
  @media (max-width: 600px) {
    padding: 12px 12px 12px 16px;
  }
`;
const Editor = styled.div<{ $split: boolean; $pane: "params" | "deco" }>`
  display: grid;
  min-height: 0;
  min-width: 0;
  grid-template-columns: ${(p) =>
    p.$split ? "minmax(340px, 380px) minmax(0, 1fr)" : "minmax(240px, 320px) minmax(0, 1fr)"};
  grid-template-rows: minmax(0, 1fr);
  .left {
    display: grid;
    align-content: start;
    gap: 18px;
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 18px 20px 18px 24px;
    border-right: 1px solid ${(p) => p.theme.colors.border};
    background: ${(p) => p.theme.colors.surface};
  }
  .right {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    min-height: 0;
    min-width: 0;
    padding: 20px 24px 16px 24px;
    &.plain {
      grid-template-rows: none;
      align-content: start;
      overflow-y: auto;
    }
  }
  .panes {
    display: none;
  }
  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto ${(p) => (p.$split ? "auto " : "")}minmax(0, 1fr);
    grid-template-areas: ${(p) =>
      p.$split ? '"preview" "tabs" "main"' : '"preview" "main"'};
    .left {
      display: contents;
    }
    .panes {
      grid-area: tabs;
      display: flex;
      gap: 4px;
      padding: 8px 12px;
      border-bottom: 1px solid ${(p) => p.theme.colors.border};
      button {
        flex: 1 1 0;
        min-height: 40px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 650;
        color: ${(p) => p.theme.colors.textSecondary};
        &[aria-selected="true"] {
          background: ${(p) => p.theme.colors.surfaceHover};
          color: ${(p) => p.theme.colors.text};
          box-shadow: inset 0 -2px 0 ${(p) => p.theme.colors.primary};
        }
      }
    }
    /* Settings живёт внутри .left (display: contents) и сам становится областью main. */
    .left > :nth-child(2) {
      grid-area: main;
      overflow-y: auto;
      min-height: 0;
      padding: 16px;
      ${(p) => (p.$split && p.$pane === "deco" ? "display: none;" : "")}
    }
    .right {
      grid-area: main;
      padding: 12px 12px 12px 16px;
      ${(p) => (p.$split && p.$pane === "params" ? "display: none;" : "")}
    }
  }
`;

interface Props {
  /** Редактирование сохранённого предмета; без него — добавление. */
  initial?: Saved;
  /** Весь инвентарь: нужен, чтобы показать, какой предмет будет заменён на стороне. */
  inventory?: Saved[];
  /** Стороны, отмеченные по умолчанию для нового предмета (по активной вкладке инвентаря). */
  defaultTeams?: number[];
  isPremium: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
}

const padStickers = (s: string[] = []) => {
  const a = [...s];
  while (a.length < 5) a.push("");
  return a.slice(0, 5);
};
const firstSlot = (stickers: string[]): SlotPick => {
  const i = stickers.findIndex((x) => !x);
  return i < 0 ? 0 : i;
};
const defaultWear = (item: Item) => {
  const opts = availableWears(item);
  return opts.includes("factory_new") ? "factory_new" : opts[0] || "factory_new";
};

/** Один диалог: каталог → настройка предмета. Слоты, каталог украшений и кнопки не перекрывают друг друга. */
export default function AddSkinDialog({ initial, inventory = [], defaultTeams, isPremium, onClose, onSaved }: Props) {
  const { t } = useLanguage();
  const [kind, setKind] = useState<(typeof KINDS)[number]>("weapon");
  const [filters, setFilters] = useState<Record<string, CatalogFilters>>({});
  const [item, setItem] = useState<Item | null>(initial?.item || null);
  const [editID] = useState(initial?.id || "");
  const savedTeams = useMemo(
    () => (initial ? initial.teams.filter((x) => initial.item.allowedTeams?.includes(x)) : []),
    [initial],
  );
  const [teams, setTeams] = useState<number[]>(savedTeams);
  const [wear, setWear] = useState(initial?.condition || (initial ? "" : "factory_new"));
  const [stickers, setStickers] = useState<string[]>(padStickers(initial?.stickers));
  const [charm, setCharm] = useState(initial?.charm || "");
  const [decorations, setDecorations] = useState<Record<string, Item>>(
    initial?.decorations || {},
  );
  const [pick, setPick] = useState<SlotPick>(() =>
    initial?.item.kind === "weapon" ? firstSlot(padStickers(initial.stickers)) : 0,
  );
  const [pane, setPane] = useState<"params" | "deco">("deco");
  // Поиск и страница каталога живут отдельно для наклеек и брелков и не теряются при смене слота.
  const [deco, setDeco] = useState<{ sticker: CatalogFilters; charm: CatalogFilters }>({
    sticker: EMPTY_FILTERS,
    charm: EMPTY_FILTERS,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [baseline, setBaseline] = useState(() =>
    JSON.stringify([[...savedTeams].sort(), initial?.condition || (initial ? "" : "factory_new"), padStickers(initial?.stickers), initial?.charm || ""]),
  );

  const kindFilters = filters[kind] || EMPTY_FILTERS;
  const snapshot = JSON.stringify([[...teams].sort(), wear, stickers, charm]);
  const dirty = !!item && snapshot !== baseline;
  const isNew = !editID;
  const weapon = item?.kind === "weapon";
  const wearOptions = useMemo(() => (item ? availableWears(item) : []), [item]);

  function chooseItem(i: Item) {
    setItem(i);
    const allowed = [...(i.allowedTeams || [TEAM_T])];
    const preset = (defaultTeams || []).filter((x) => allowed.includes(x));
    const all = preset.length ? preset : allowed;
    const w = defaultWear(i);
    setTeams(all);
    setStickers(padStickers());
    setCharm("");
    setDecorations({});
    setWear(w);
    setPick(0);
    setPane("deco");
    setBaseline(JSON.stringify([[...all].sort(), w, padStickers(), ""]));
    setError("");
  }

  function chooseDecoration(d: Item) {
    setDecorations((cur) => ({ ...cur, [d.id]: d }));
    if (pick === "charm") setCharm(d.id);
    else
      setStickers((s) => {
        const a = padStickers(s);
        a[pick] = d.id;
        return a;
      });
  }

  const clearSticker = (slot: number) =>
    setStickers((s) => {
      const a = padStickers(s);
      a[slot] = "";
      return a;
    });

  async function save() {
    if (!item) return;
    setBusy(true);
    setError("");
    try {
      const d = await request<{ id: string }>("/skins", {
        id: editID,
        catalogId: item.id,
        teams,
        stickers: item.kind === "weapon" ? stickers : [],
        charm: item.kind === "weapon" ? charm : "",
        // Пустое значение не меняет сохранённое состояние.
        condition: wearOptions.length === 0 ? (isNew ? wear : "") : wear,
      });
      onSaved(d.id || editID);
    } catch (e) {
      // Черновик остаётся как есть: можно исправить и повторить.
      setError((e as Error).message);
      setBusy(false);
    }
  }

  const name = item ? splitName(item.name) : { model: "", skin: "" };
  const heading = item ? (name.skin ? `${name.model} | ${name.skin}` : name.model) : t("skins.add_title");

  // Предметы, которые сейчас стоят на сторонах вместо редактируемого.
  const occupant: Record<number, string> = {};
  if (item) {
    for (const team of [2, 3]) {
      const other = inventory.find(
        (s) => s.id !== editID && slotOf(s.item) === slotOf(item) && s.teams.includes(team),
      );
      if (other) occupant[team] = other.item.name;
    }
  }
  const noSide = isNew && teams.length === 0;
  const unknownWear = wearOptions.length > 0 && !wearOptions.includes(wear);
  const missing = noSide || (isNew && unknownWear);

  const footer = !item ? (
    <Action type="button" onClick={onClose}>
      {t("common.cancel")}
    </Action>
  ) : (
    <>
      {error && (
        <p role="alert" style={{ flex: "1 1 100%", margin: 0, color: "#ff9191" }}>
          {error}
        </p>
      )}
      {!isPremium && (
        <span style={{ flex: "1 1 auto", fontSize: 14, color: "#939dac" }}>
          {t("skins.need_premium")} <Link to="/subscription">{t("common.premium")}</Link>
        </span>
      )}
      {isNew && (
        <Action
          type="button"
          disabled={busy}
          style={{ marginRight: "auto" }}
          onClick={() => setItem(null)}
        >
          <FiArrowLeft aria-hidden="true" /> {t("skins.back_to_catalog")}
        </Action>
      )}
      <Action type="button" disabled={busy} onClick={onClose}>
        {t("common.cancel")}
      </Action>
      <Action
        type="button"
        $variant="primary"
        disabled={busy || !isPremium || missing || (!isNew && !dirty)}
        aria-busy={busy}
        style={{ minWidth: 190 }}
        onClick={save}
      >
        {busy ? t("skins.saving") : isNew ? t("skins.add_apply") : t("skins.save_apply")}
      </Action>
    </>
  );

  const settings = item && (
    <SkinSettings
      item={item}
      isNew={isNew}
      teams={teams}
      savedTeams={savedTeams}
      onTeams={setTeams}
      occupant={occupant}
      wear={wear}
      wearOptions={wearOptions}
      onWear={setWear}
      busy={busy}
    />
  );

  return (
    <Dialog
      size="xl"
      fill={!item || !!weapon}
      flush
      label={item ? `${t("skins.editor_title")}: ${item.name}` : t("skins.add_title")}
      onClose={onClose}
      dirty={dirty}
      footer={footer}
    >
      <Shell>
        <Head>
          <div className="eyebrow">{item ? t("skins.editor_title") : t("skins.add_title")}</div>
          {item ? (
            <h2>{heading}</h2>
          ) : (
            <Tabs role="tablist" aria-label={t("skins.kind")}>
              {KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={kind === k}
                  onClick={() => setKind(k)}
                >
                  {t(`skins.kind.${k}`)}
                </button>
              ))}
            </Tabs>
          )}
        </Head>

        {!item ? (
          <CatalogArea>
            <CatalogPanel
              kind={kind}
              withModels
              autoFocus
              searchLabel={t("skins.search_name")}
              filters={kindFilters}
              onFilters={(f) => setFilters((cur) => ({ ...cur, [kind]: f }))}
              onPick={chooseItem}
            />
          </CatalogArea>
        ) : (
          <Editor $split={!!weapon} $pane={pane}>
            <div className="left">
              <SkinPreview item={item} teams={teams} wear={wear || undefined} />
              {weapon && settings}
            </div>
            {weapon && (
              <div className="panes" role="tablist" aria-label={t("skins.editor_title")}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={pane === "params"}
                  onClick={() => setPane("params")}
                >
                  {t("skins.pane_params")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={pane === "deco"}
                  onClick={() => setPane("deco")}
                >
                  {t("skins.pane_deco")}
                </button>
              </div>
            )}
            {weapon ? (
              <div className="right">
                <DecorationSlots
                  stickers={stickers}
                  charm={charm}
                  decorations={decorations}
                  active={pick}
                  onPick={setPick}
                  onClearSticker={clearSticker}
                  onClearCharm={() => setCharm("")}
                  busy={busy}
                />
                <CatalogPanel
                  key={pick === "charm" ? "charm" : "sticker"}
                  kind={pick === "charm" ? "charm" : "sticker"}
                  dense
                  searchLabel={
                    pick === "charm" ? t("skins.search_charm") : t("skins.search_sticker")
                  }
                  filters={pick === "charm" ? deco.charm : deco.sticker}
                  onFilters={(f) =>
                    setDeco((cur) => (pick === "charm" ? { ...cur, charm: f } : { ...cur, sticker: f }))
                  }
                  selectedId={pick === "charm" ? charm : stickers[pick]}
                  onPick={chooseDecoration}
                />
              </div>
            ) : (
              <div className="right plain">
                {settings}
              </div>
            )}
          </Editor>
        )}
      </Shell>
    </Dialog>
  );
}
